import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import decode from 'entity-decode';
import { useSetRecoilState } from 'recoil';
import useSWR from 'swr';
import { css } from '@emotion/react';
import _ from 'lodash';
import { SwButtonSection, getFetcher, ADMIN_ACCESS_TOKEN_KEY } from '../../../App';
import { PrimaryButton, Sub1Button } from '@components/atoms';
import { dialogContentsState } from '@components/templates';
import { PacsSettingField } from '@components/templates';
import SettingCommonMethods from '../../../utils/settingCommon';
import { PacsAetInfoWrap, PacsAetTitle, PacsAetValue, PacsServerSettingsBody, PacsServerSettingsFooter } from './style';
import { submitDebounce } from '../../../utils/lib';

type PacsInfo = {
  aet: string;
  forwardPacsAET: string | null;
  forwardPacsIpAddress: string;
  forwardPacsPort: string;
  id: number;
  ipAddress: string;
  port: string;
  protocol: string;
  valid?: string;
  forwardValid?: string;
};
const PacsCloudSettingPage = () => {
  const random = React.useRef(Date.now());
  const [accessToken, setAccessToken] = useState('');
  const [isChanged, setIsChanged] = useState(false);
  const [settingFields, setSettingFields] = useState<any[]>([]);
  const [originSettingFields, setOriginSettingFields] = useState<any[]>([]);
  const [baseData, setBaseData] = useState<any>(null);
  const [baseAetData, setBaseAetData] = useState<any>(null);
  const setDialogContents = useSetRecoilState(dialogContentsState);
  const { errorHandler } = SettingCommonMethods();

  // pacs 설정값 SWR로 가져오기
  const url = `api/sw/pacs`;
  const { data, error, mutate } = useSWR(!!accessToken.length ? [{ url, accessToken }, random] : null);

  // AET , PORT 정보 가져오기
  const urlAET = `api/sw/pacs/me`;
  const aetResult = useSWR(!!accessToken.length ? [{ url: urlAET, accessToken }] : null);
  const aetData = aetResult.data;
  const aetError = aetResult.error;

  /**
   * pacs echo 체크
   * @param idx
   */
  const checkPacsEcho = async (idx: number, type?: string) => {
    // valid 체크

    const list = [...settingFields];
    const originList = [...originSettingFields];

    const _params = {
      aet: settingFields[idx].aet,
      ipAddress: settingFields[idx].ipAddress,
      port: Number(settingFields[idx].port),
      protocol: settingFields[idx].protocol,
    };

    if (type === 'FORWARD') {
      _params.aet = settingFields[idx].forwardPacsAET;
      _params.ipAddress = settingFields[idx].forwardPacsIpAddress;
      _params.port = Number(settingFields[idx].forwardPacsPort);
      list[idx].forwardValid = 'process';
      if (originList[idx]) originList[idx].forwardValid = 'process';
      setSettingFields(list);
    } else {
      list[idx].valid = 'process';
      if (originList[idx]) originList[idx].valid = 'process';
      setSettingFields(list);
    }

    const url = `api/sw/pacs/echo`;
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: _params,
    })(url)
      .then(async (r) => {
        const list = [...settingFields];
        const originList = [...originSettingFields];
        const _result = r.code === '200' && r.result;
        const _resultText = _result ? 'success' : 'failed';

        if (type === 'FORWARD') {
          list[idx].forwardValid = _resultText;
          if (originList[idx]) originList[idx].forwardValid = _resultText;
        } else {
          list[idx].valid = _resultText;
          if (originList[idx]) originList[idx].valid = _resultText;
        }

        if (!_result) isFailedEchoLayer();
        await setSettingFields(list);
      })
      .catch((e) => {
        const list = [...settingFields];
        const originList = [...originSettingFields];
        list[idx].valid = 'failed';
        list[idx].forwardValid = 'failed';
        if (originList[idx]) originList[idx].valid = 'failed';
        if (originList[idx]) originList[idx].forwardValid = 'failed';
        isFailedEchoLayer();
        setSettingFields(list);
      });
  };

  /**
   * echo 실패 레이어
   */
  const isFailedEchoLayer = () => {
    setDialogContents({
      type: 'plain',
      body: 'The integration failed.',
      footer: (
        <>
          <PrimaryButton
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            OK
          </PrimaryButton>
        </>
      ),
    });
  };

  /**
   * pacs 데이터 저장
   */
  const onSubmit = async () => {
    const url = `api/sw/pacs`;
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;
    const _params: any = [];
    settingFields.forEach((data: any) => {
      const _tmp = {
        aet: data.aet,
        ipAddress: data.ipAddress,
        port: data.port,
        protocol: data.protocol,
        forwardPacsIpAddress: data.forwardPacsIpAddress || null,
        forwardPacsPort: data.forwardPacsPort || null,
        forwardPacsAET: data.forwardPacsAET || null,
        ...(data.id ? { id: data.id } : {}),
      };
      _params.push(_tmp);
    });

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: _params,
    })(url)
      .then(async (r) => {
        if (r.code === '200') {
          // setOriginSettingFields(settingFields);
          setDialogContents({
            type: 'plain',
            body: 'It has been reflected.',
            footer: (
              <>
                <PrimaryButton
                  onClick={async () => {
                    setDialogContents({ type: 'hidden' });
                  }}
                >
                  OK
                </PrimaryButton>
              </>
            ),
          });
          mutate();
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * pacs 데이터 유효성 체크
   * @param settingFields
   * @param originSettingFields
   */
  const checkValidAllClear = (settingFields: any, originSettingFields: any) => {
    const sameData = _.isEqual(settingFields, originSettingFields);

    if (!sameData) {
      let count = 0;
      // 백업 데이터와 같지 않으며
      for (let i = 0; i < settingFields.length; i++) {
        const _target = settingFields[i];
        // 필수값 (from ip // port  있는가?)
        if (
          // !_target.aet ||
          !_target.ipAddress ||
          !_target.port ||
          !_target.protocol
          // !_target.forwardPacsIpAddress ||
          // !_target.forwardPacsPort ||
          // !_target.protocol ||
          // !_target.forwardPacsAET
        ) {
          count = count + 1;
        } else {
          // 필수 값이 다 있을 경우 현재 보는 데이터에 ipAddress, port 가 같은 값이 존재 하는가?
          for (let j = 0; j < settingFields.length; j++) {
            const _subTarget = settingFields[j];
            if (
              i !== j &&
              // _target.aet === _subTarget.aet &&
              _target.ipAddress === _subTarget.ipAddress &&
              _target.port === _subTarget.port
            ) {
              count = count + 1;
            }
          }
        }
      }
      return count === 0;
    } else {
      // 백업 데이터와 같으면 그냥 false
      return false;
    }
  };

  /**
   * 리스트 추가
   */
  const addSettingComponent = () => {
    setSettingFields((prev: any) => {
      return [
        ...prev,
        {
          id: null,
          aet: '',
          ipAddress: '',
          port: '',
          protocol: 'NONE',
          valid: 'ready',
          forwardPacsIpAddress: '',
          forwardPacsPort: '',
          forwardPacsAET: '',
          forwardValid: 'ready',
        },
      ];
    });
  };

  /**
   * 리스트 복사
   */
  const copySettingComponent = useCallback(
    async (idx: number) => {
      const list = [...settingFields];

      await setSettingFields((prev: any) => {
        return [
          ...prev,
          {
            id: null,
            aet: list[idx].aet,
            ipAddress: list[idx].ipAddress,
            port: list[idx].port,
            protocol: list[idx].protocol,
            valid: 'ready',
            forwardPacsIpAddress: list[idx].forwardPacsIpAddress || '',
            forwardPacsPort: list[idx].forwardPacsPort || '',
            forwardPacsAET: list[idx].forwardPacsAET || '',
            forwardValid: 'ready',
          },
        ];
      });
      scrollToBottom();
    },
    [settingFields],
  );

  /**
   * 리스트 삭제 컨펌
   * @param deletedId
   */
  const confirmDeleteList = (deletedIdx: number) => {
    setDialogContents({
      type: 'plain',
      body: 'Do you want to delete it?.',
      footer: (
        <>
          <Sub1Button
            onClick={async () => {
              setDialogContents({ type: 'hidden' });
            }}
          >
            Cancel
          </Sub1Button>
          <PrimaryButton
            onClick={async () => {
              closeSettingComponent(deletedIdx);
              setDialogContents({ type: 'hidden' });
            }}
          >
            Delete
          </PrimaryButton>
        </>
      ),
    });
  };

  /**
   * 리스트 삭제
   * @param deletedIdx
   */
  const closeSettingComponent = (deletedIdx: number) => {
    const list = [...settingFields];
    list.splice(deletedIdx, 1);
    setSettingFields(list);
  };

  /**
   * 리스트 값 변경
   * @param e
   * @param order
   */
  const onChangeSettingField = (e: any, order: number) => {
    const list = [...settingFields];
    const originList = [...originSettingFields];

    // from 인풋 적용
    if (e.target.name.indexOf('forward') === -1) {
      if (e.target.name.split('_')[0] === 'valid') {
        list[order].valid = 'success';
        if (originList[order]) originList[order].valid = 'success';
      } else {
        list[order][e.target.name.split('_')[0]] = e.target.value;
        list[order].valid = 'ready';
        if (originList[order]) originList[order].valid = 'ready';
      }
    } else {
      // to 인풋 적용
      if (e.target.name.split('_')[0] === 'forwardValid') {
        list[order].forwardValid = 'success';
        if (originList[order]) originList[order].forwardValid = 'success';
      } else {
        list[order][e.target.name.split('_')[0]] = e.target.value;
        list[order].forwardValid = 'ready';
        if (originList[order]) originList[order].forwardValid = 'ready';
      }
    }

    setSettingFields(list);
  };

  /**
   * 스크롤 이동
   */
  const scrollToBottom = () => {
    const target = document.querySelector('.list-wrap');
    target?.scrollTo({ top: target.scrollHeight, left: 0, behavior: 'smooth' });
  };

  /**
   * 데이터 변경 감지 submit 상태 여부
   */
  useEffect(() => {
    // echo 성공 여부 및 데이터 유효성
    const _changePacDataList = checkValidAllClear(settingFields, originSettingFields);
    setIsChanged(_changePacDataList);
  }, [settingFields, originSettingFields]);

  // 진입시 토큰 저장
  useEffect(() => {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    if (newToken) {
      setAccessToken(newToken);
    }
  }, []);

  /**
   * 데이터 갱신시 기본 변수 설정
   */
  useEffect(() => {
    setBaseData(data);
    setBaseAetData(aetData);
  }, [data, aetData]);

  // swr 데이터 갱신
  useEffect(() => {
    if (baseData?.result) {
      const deepCopiedList = JSON.parse(
        JSON.stringify(
          baseData.result.map((pacsInfo: PacsInfo) => ({
            ...pacsInfo,
            aet: decode(pacsInfo.aet),
            forwardPacsIpAddress: pacsInfo.forwardPacsIpAddress || '',
            forwardPacsAET: decode(pacsInfo.forwardPacsAET || ''),
            forwardPacsPort: pacsInfo.forwardPacsPort || '',
            forwardValid:
              pacsInfo.forwardPacsIpAddress && pacsInfo.forwardPacsAET && pacsInfo.forwardPacsPort ? 'ready' : '',
            valid: 'ready',
          })) || [],
        ),
      );
      setSettingFields(deepCopiedList);
      setOriginSettingFields(JSON.parse(JSON.stringify(deepCopiedList)));
    } else {
      const _tmp = [
        {
          id: null,
          aet: '',
          ipAddress: '',
          port: '',
          protocol: 'NONE',
          valid: 'ready',
          forwardPacsIpAddress: '',
          forwardPacsPort: '',
          forwardPacsAET: '',
          forwardValid: 'ready',
        },
      ];
      setSettingFields(_tmp);
      setOriginSettingFields(_tmp);
    }
  }, [baseData]);

  if (error || aetError) return <div>failed to load</div>;
  if (!baseData || !baseAetData) return <div>loading...</div>;
  if (!baseData.result || !baseAetData.result)
    return (
      <div>
        <>Error : {baseData.code || 'unknown'} </>
      </div>
    );

  return (
    <>
      <label>PACS(cloud)</label>

      <PacsAetInfoWrap>
        <PacsAetTitle>Heuron AE Title</PacsAetTitle>
        <PacsAetValue>{baseAetData.result.aet}</PacsAetValue>
        <PacsAetTitle>Port Number</PacsAetTitle>
        <PacsAetValue>{baseAetData.result.port}</PacsAetValue>
      </PacsAetInfoWrap>

      <PacsServerSettingsBody className={'list-wrap'}>
        {settingFields.length > 0 &&
          settingFields.map((settingField, index) => (
            <PacsSettingField
              key={`settingField_${index}`}
              displayOrder={index}
              settingFieldCount={settingFields.length}
              onEcho={checkPacsEcho}
              onChange={onChangeSettingField}
              onClose={confirmDeleteList}
              onCopy={copySettingComponent}
              dataCloud={settingField}
              type="CLOUD"
            />
          ))}
      </PacsServerSettingsBody>
      <PacsServerSettingsFooter onClick={() => addSettingComponent()}>Add information</PacsServerSettingsFooter>
      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            font-size: 18px;
          `}
          onClick={() => {
            setDialogContents({
              type: 'plain',
              body: 'Do you want to save the Changes?',
              footer: (
                <>
                  <Sub1Button onClick={() => setDialogContents({ type: 'hidden' })}>Cancel</Sub1Button>
                  <PrimaryButton
                    onClick={submitDebounce(() => {
                      onSubmit();
                      setDialogContents({ type: 'hidden' });
                    })}
                  >
                    Save
                  </PrimaryButton>
                </>
              ),
            });
          }}
          disabled={!isChanged}
        >
          Submit
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default PacsCloudSettingPage;
